import { Link, useLangNavigate } from "@/components/LangLink";
import { ArrowRight, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";
import { useLoginModal, useAuth } from "@/App";
import { IMAGES, MEMBERSHIPS } from "@/data/site";
import { tr } from "@/i18n";

export default function Membership() {
  const openLogin = useLoginModal();
  const { user } = useAuth();
  const navigate = useLangNavigate();

  return (
    <div data-testid="membership-page">
      <PageHero
        overline={tr("Delux Concierge Memberships")}
        title={tr("One of life's most")}
        italic={tr("high-powered networks.")}
        image={IMAGES.chauffeur}
      >
        <p>{tr("Become part of an exclusive circle — and no matter what the request, we will deliver. Membership is limited and granted by application only.")}</p>
      </PageHero>

      <section className="py-28 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {MEMBERSHIPS.map((m, i) => (
              <Reveal key={m.id} delay={i * 0.15}>
                <article
                  id={m.id}
                  data-testid={`membership-card-${m.id}`}
                  className={`relative h-full border p-10 lg:p-14 flex flex-col transition-colors duration-700 scroll-mt-28 ${
                    m.id === "individual"
                      ? "border-gold/50 bg-[#0B0A08]"
                      : "border-white/12 bg-[#080808] hover:border-white/25"
                  }`}
                >
                  {m.id === "individual" && (
                    <span className="absolute -top-3 left-10 bg-gold text-black text-[0.6rem] uppercase tracking-[0.3em] px-4 py-1.5">{tr("Signature")}</span>
                  )}
                  <span className="font-serif italic text-white/30 text-lg">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-serif text-4xl lg:text-5xl font-light mt-4">{tr(m.title)}</h2>
                  <p className="text-white/55 font-light leading-relaxed mt-6">{tr(m.intro)}</p>
                  <ul className="mt-10 space-y-4 flex-1">
                    {m.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-4 text-white/70 font-light">
                        <Check strokeWidth={1.5} size={16} className="text-gold mt-1 shrink-0" />
                        {tr(b)}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/contact?type=admission&tier=${m.id}`}
                    data-testid={`membership-apply-${m.id}`}
                    className={`group mt-12 inline-flex items-center justify-center gap-3 text-xs uppercase tracking-[0.3em] px-8 py-4 transition-colors duration-500 ${
                      m.id === "individual"
                        ? "bg-gold text-black hover:bg-gold-bright"
                        : "border border-white/25 text-white hover:border-gold hover:text-gold"
                    }`}
                  >{tr("Request for Admission")}<ArrowRight strokeWidth={1.5} size={14} className="transition-transform duration-400 group-hover:translate-x-1" />
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-20 border border-white/10 bg-black/40 backdrop-blur-xl px-10 py-12 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <Overline className="mb-3">{tr("Existing Members")}</Overline>
                <p className="font-serif text-2xl lg:text-3xl font-light">{tr("Your portal awaits —")}<em className="gold-shimmer italic">{tr("sign in to continue.")}</em>
                </p>
              </div>
              <button
                data-testid="membership-signin-button"
                onClick={user ? () => navigate("/portal") : openLogin}
                className="border border-gold/70 text-gold text-xs uppercase tracking-[0.3em] px-10 py-4 hover:bg-gold hover:text-black transition-colors duration-500 shrink-0"
              >
                {user ? tr("My Portal") : tr("Member Login")}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory text-navy py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <img src="/logo-navy.png" alt={tr("Delux Concierge")} className="h-28 w-auto mx-auto mb-10" />
            <h2 className="font-serif font-light text-3xl sm:text-4xl leading-snug">{tr("\"We understand how valuable your time is — so we work tirelessly to ensure every request is met")}<em className="italic">{tr("expeditiously.")}</em>"
            </h2>
            <div className="mt-10 flex items-center justify-center gap-6">
              <span className="h-px w-12 bg-navy/40" />
              <span className="text-xs uppercase tracking-[0.3em] text-navy/50">{tr("The Delux Standard")}</span>
              <span className="h-px w-12 bg-navy/40" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
